import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const ExtrasTable = ({ extras, loading, total }) => {
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const columns = [
    { Header: 'Tipo', accessor: 'tipo_extra.tipo' },
    { Header: 'Extra', accessor: 'extra' },
    { Header: 'Monto Sugerido', accessor: 'monto' }
  ];

  // Calcular datos paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return extras.slice(startIndex, endIndex);
  }, [extras, currentPage, itemsPerPage]);

  // Calcular información de paginación
  const totalPages = Math.ceil(extras.length / itemsPerPage);
  const startItem = extras.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, extras.length);

  // Manejar cambio de página
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Renderizar cabecera
  const renderHeader = () => (
    <tr>
      {columns.map(col => (
        <th key={col.accessor} style={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '2px solid #dee2e6',
          padding: '12px',
          fontWeight: '600',
          color: '#495057'
        }}>
          {col.Header}
        </th>
      ))}
    </tr>
  );

  // Formatear monto con separadores de miles y aplicar lógica de colores
  const formatMonto = (monto, tipoExtra) => {
    if (!monto && monto !== 0) return '';
    
    // Determinar si es BONO o no
    const esBono = tipoExtra?.tipo?.toUpperCase() === 'BONOS';
    const montoFinal = esBono ? monto : -Math.abs(monto);
    
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montoFinal);
  };

  // Obtener color del monto según el tipo
  const getMontoColor = (tipoExtra) => {
    const esBono = tipoExtra?.tipo?.toUpperCase() === 'BONOS';
    return esBono ? '#28a745' : '#dc3545'; // Verde para BONO, rojo para otros
  };

  // Renderizar filas
  const renderRows = () => (
    paginatedData.map((row, idx) => (
      <tr key={row.id || idx} style={{ 
        cursor: 'default',
        borderBottom: '1px solid #dee2e6'
      }}>
        <td style={{ padding: '12px', color: '#495057' }}>
          <span style={{
            backgroundColor: '#e9ecef',
            color: '#495057',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            textTransform: 'uppercase'
          }}>
            {row.tipo_extra?.tipo || 'Sin tipo'}
          </span>
        </td>
        <td style={{ padding: '12px', color: '#212529', fontWeight: '500' }}>
          {row.extra || ''}
        </td>
        <td style={{ 
          padding: '12px', 
          color: getMontoColor(row.tipo_extra), 
          fontWeight: '600',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          {formatMonto(row.monto, row.tipo_extra)}
        </td>
      </tr>
    ))
  );

  // Renderizar información de totales y paginación
  const renderTotalInfo = () => (
    <div style={{
      padding: '12px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #dee2e6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '14px',
      color: '#6c757d'
    }}>
      <span>
        Mostrando {startItem} a {endItem} de <strong>{extras.length}</strong> extra{extras.length !== 1 ? 's' : ''}
      </span>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ fontSize: '12px', padding: '4px 8px' }}
          >
            Anterior
          </button>
          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            Página {currentPage} de {totalPages}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ fontSize: '12px', padding: '4px 8px' }}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ 
      width: '100%', 
      overflowX: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      backgroundColor: 'white'
    }}>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status" style={{ marginBottom: '16px' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <div style={{ color: '#6c757d', fontSize: '14px' }}>
              Cargando extras...
            </div>
          </div>
        </div>
      ) : (
        <>
          <table className="table" style={{ 
            minWidth: '600px', 
            margin: '0',
            backgroundColor: 'white'
          }}>
            <thead>{renderHeader()}</thead>
            <tbody>
              {paginatedData.length > 0 ? renderRows() : (
                <tr>
                  <td colSpan={columns.length} style={{ 
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                    fontSize: '16px'
                  }}>
                    <div>
                      <i className="fas fa-search" style={{ 
                        fontSize: '2rem', 
                        marginBottom: '12px',
                        color: '#dee2e6'
                      }}></i>
                      <div>No se encontraron extras</div>
                      <small style={{ color: '#adb5bd' }}>
                        Intenta ajustar los filtros de búsqueda
                      </small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {paginatedData.length > 0 && renderTotalInfo()}
        </>
      )}
    </div>
  );
};

ExtrasTable.propTypes = {
  extras: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  total: PropTypes.number.isRequired
};

ExtrasTable.defaultProps = {
  loading: false
};

export default ExtrasTable;
